import DefaultFormHeaderImage from '@/assets/images/form_default_header_image.png'
import ActionCard from '@/components/ActionCard'
import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
} from '@/components/ui/alert-dialog'
import { Button, ButtonText } from '@/components/ui/button'
import { Divider } from '@/components/ui/divider'
import { HStack } from '@/components/ui/hstack'
import { Icon } from '@/components/ui/icon'
import { Image } from '@/components/ui/image'
import { Input, InputField } from '@/components/ui/input'
import { Text } from '@/components/ui/text'
import { VStack } from '@/components/ui/vstack'
import useDefaultToast from '@/components/useDefaultToast'
import { cn } from '@/lib/cn'
import { openShare } from '@/lib/share'
import getHTMLText from '@/lib/utils/getHTMLText'
import setImageUrlResizeParams from '@/lib/utils/imageUrlResizeParams'
import { isBelowiOS18 } from '@/lib/utils/iosCompat'
import PlatformDetection from '@/lib/utils/platformDetection'
import { shareWebpage, ShareWebpageResult, WechatScene } from '@/lib/wechat'
import { selectPublishedFormHostUrl } from '@/redux/slices/formBillingAccountSlice'
import { formSelectors, ShallowForm } from '@/redux/slices/formSlice'
import { useAppSelector } from '@/redux/store'
import { ThemeHeaderType } from '@/typings/domain'
import Clipboard from '@react-native-clipboard/clipboard'
import * as Sentry from '@sentry/react-native'
import { noop } from 'lodash-es'
import { ApertureIcon, LinkIcon, QrCodeIcon, ShareIcon } from 'lucide-react-native'
import { useMemo, useRef, useState } from 'react'
import { ImageSourcePropType, Platform, View } from 'react-native'
import QRCode from 'react-native-qrcode-svg'
import saveFormQrCode from './saveFormQrCode'

const isWeixinBrowser =
  Platform.OS === 'web' && typeof navigator !== 'undefined'
    ? new PlatformDetection(navigator.userAgent).isWeixinBrowser()
    : false

export default function FormPublish({ formToken }: { formToken: string }) {
  const form = useAppSelector(state => formSelectors.selectById(state.forms, formToken))

  const publishedFormHostUrl = useAppSelector(state => selectPublishedFormHostUrl(state, formToken))
  const publishedFormUrl = useMemo(() => `${publishedFormHostUrl}/f/${formToken}`, [publishedFormHostUrl, formToken])

  if (!form) return null

  return (
    <VStack space="xl" className={isBelowiOS18 ? 'pb-20' : ''}>
      {Platform.OS === 'web' ? (
        <FormPublishWeb form={form} publishedFormUrl={publishedFormUrl} formToken={formToken} />
      ) : (
        <>
          <FormCardPreview form={form} />
          <ShareToWeixinFriends form={form} publishedFormUrl={publishedFormUrl} />
          <OtherShareOptions form={form} publishedFormUrl={publishedFormUrl} />
        </>
      )}
    </VStack>
  )
}

type ShareToWeixinFriendsProps = {
  form: ShallowForm
  publishedFormUrl: string
}

function ShareToWeixinFriends({ publishedFormUrl, form }: ShareToWeixinFriendsProps) {
  const toast = useDefaultToast()

  const handleShareToWeixinFriendsPress = async () => {
    const coverUrl = form.setting?.weixinThumbnail?.url
      ? setImageUrlResizeParams(form.setting.weixinThumbnail.url, { w: 100 })
      : undefined

    // coverUrl 文件大小不能超过 64k，否则会分享失败
    const result = await shareWebpage({
      title: form.name ?? '',
      description: getHTMLText(form.description) ?? '',
      scene: WechatScene.Session,
      webpageUrl: publishedFormUrl,
      coverUrl,
    })

    if (!result.success) {
      reportWechatShareFailure(result, { scene: 'Session', formToken: form.token, coverUrl })
      toast.show(buildShareFailureMessage(result))
    }
  }

  return (
    <Button className="rounded-3xl h-12" onPress={handleShareToWeixinFriendsPress}>
      <ButtonText size="xl">分享给微信好友</ButtonText>
    </Button>
  )
}

function buildShareFailureMessage(result: Extract<ShareWebpageResult, { success: false }>) {
  if (result.error === 'unavailable') return '分享失败：微信组件不可用'
  if (result.message) return `分享失败：${result.message}`
  return '分享失败'
}

function reportWechatShareFailure(
  result: Extract<ShareWebpageResult, { success: false }>,
  extra: { scene: 'Session' | 'Timeline'; formToken: string; coverUrl?: string }
) {
  Sentry.captureMessage('Wechat share failed', {
    level: 'warning',
    extra: {
      error: result.error,
      message: result.message ?? null,
      hasCoverUrl: !!extra.coverUrl,
      coverUrl: extra.coverUrl ?? null,
      scene: extra.scene,
      formToken: extra.formToken,
    },
  })
}

type FormPublishPropsWeb = {
  form: ShallowForm
  formToken: string
  publishedFormUrl: string
}

function FormPublishWeb({ form, formToken, publishedFormUrl }: FormPublishPropsWeb) {
  const toast = useDefaultToast()
  const qrCodeRef = useRef<QRCodeRef>(null)
  const [savePreviewUri, setSavePreviewUri] = useState<string | null>(null)

  const handleSaveQRCodePress = async () => {
    const qrCodeDataURI = await new Promise<string>(resolve =>
      qrCodeRef.current?.toDataURL(resolve, { width: 600, height: 600 })
    )
    if (isWeixinBrowser) {
      setSavePreviewUri(`data:image/png;base64,${qrCodeDataURI}`)
      return
    }
    saveFormQrCode({ filename: `${formToken}-${Date.now()}`, dataURI: qrCodeDataURI })
  }

  const handleCopyLinkPress = () => {
    Clipboard.setString(publishedFormUrl)
    toast.show('复制成功')
  }

  const handleDirectOpenPress = () => {
    window.open(publishedFormUrl, '_blank')
  }

  const handleCloseSavePreview = () => {
    setSavePreviewUri(null)
  }

  return (
    <VStack space="lg" className="bg-white dark:bg-background-50 rounded-2xl overflow-hidden">
      <FormCardHeader form={form} />

      <Divider className="mx-5 w-auto" />

      <VStack space="md" className="items-center px-5">
        <View className="bg-white p-3 rounded-xl border border-outline-100">
          <QRCode value={publishedFormUrl} />
        </View>
        <Button size="sm" className="px-5 rounded-full" onPress={handleSaveQRCodePress}>
          <ButtonText>保存二维码</ButtonText>
        </Button>
      </VStack>

      <Divider className="mx-5 w-auto" />

      <VStack space="sm" className="px-5 pb-5">
        <Input isReadOnly variant="rounded" className="bg-background-50 border-0">
          <InputField defaultValue={publishedFormUrl} selectTextOnFocus />
        </Input>

        <HStack className="items-center justify-center">
          <Button action="primary" variant="link" className="flex-1" onPress={handleCopyLinkPress}>
            <ButtonText>复制链接</ButtonText>
          </Button>
          <Divider orientation="vertical" className="h-4" />
          <Button action="primary" variant="link" className="flex-1" onPress={handleDirectOpenPress}>
            <ButtonText>直接打开</ButtonText>
          </Button>
        </HStack>
      </VStack>

      <View className="absolute pointer-events-none opacity-0 left-0 top-0">
        <QRCode
          value={publishedFormUrl}
          getRef={ref => {
            qrCodeRef.current = ref
          }}
        />
      </View>

      <AlertDialog isOpen={!!savePreviewUri} onClose={handleCloseSavePreview} size="md">
        <AlertDialogBackdrop />
        <AlertDialogContent className="p-0">
          <AlertDialogBody className="px-5 pt-6 pb-4">
            <VStack space="md" className="items-center">
              {savePreviewUri && (
                <View className="bg-white p-3 rounded-lg w-full">
                  <Image size="none" source={{ uri: savePreviewUri }} alt="二维码" className="w-full aspect-square" />
                </View>
              )}
              <Text size="lg" bold className="text-label">
                长按图片保存到相册
              </Text>
            </VStack>
          </AlertDialogBody>
          <AlertDialogFooter className="p-0 border-t border-outline-100 justify-center">
            <Button
              variant="link"
              action="primary"
              onPress={handleCloseSavePreview}
              className="w-full h-12 rounded-none"
            >
              <ButtonText>关闭</ButtonText>
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </VStack>
  )
}

type OtherShareOptionsProps = {
  form: ShallowForm
  publishedFormUrl: string
}

type QRCodeRef = {
  toDataURL: (callback: (data: string) => void, options?: { width?: number; height?: number }) => void
}

function OtherShareOptions({ form, publishedFormUrl }: OtherShareOptionsProps) {
  const toast = useDefaultToast()
  const qrCodeRef = useRef<QRCodeRef>(null)

  const handleCopyLinkPress = () => {
    Clipboard.setString(publishedFormUrl)
    toast.show('复制成功')
  }

  const handleGenerateQRCodePress = async () => {
    const qrCodeDataURI = await new Promise<string>(resolve => qrCodeRef.current?.toDataURL(resolve))

    const result = await saveFormQrCode({ filename: `${form.token}-${Date.now()}`, dataURI: qrCodeDataURI })

    if (result.success) {
      toast.show('二维码保存成功')
    } else if (result.error === 'permission-denied') {
      toast.show('请在系统设置中开启相册权限')
    } else if (result.error === 'unknown') {
      toast.show('保存二维码失败')
    }
  }

  const handleShareToWechatTimeline = async () => {
    const coverUrl = form.setting?.weixinThumbnail?.url
      ? setImageUrlResizeParams(form.setting.weixinThumbnail.url, { w: 100 })
      : undefined

    // coverUrl 文件大小不能超过 64k，否则会分享失败
    const result = await shareWebpage({
      title: form.name ?? '',
      description: getHTMLText(form.description) ?? '',
      scene: WechatScene.Timeline,
      webpageUrl: publishedFormUrl,
      coverUrl,
    })

    if (!result.success) {
      reportWechatShareFailure(result, { scene: 'Timeline', formToken: form.token, coverUrl })
      toast.show(buildShareFailureMessage(result))
    }
  }

  const handleSystemSharePress = () => {
    openShare({
      title: form.name ?? '',
      url: publishedFormUrl,
      activityItemSources: [
        {
          placeholderItem: { type: 'url', content: publishedFormUrl },
          item: { default: { type: 'url', content: publishedFormUrl } },
          subject: { default: form.name },
          linkMetadata: { originalUrl: publishedFormUrl, url: publishedFormUrl, title: form.name },
        },
      ],
    }).catch(noop)
  }

  return (
    <VStack space="lg">
      <HStack space="lg" className="items-center">
        <Divider className="flex-1" />
        <Text size="body" className="text-secondary-label">
          其他发布方式
        </Text>
        <Divider className="flex-1" />
      </HStack>

      <HStack space="xl" className="items-center justify-center">
        <ActionCard
          detached
          className="w-16"
          title="复制链接"
          icon={<Icon as={LinkIcon} size="xl" />}
          onPress={handleCopyLinkPress}
        />
        <ActionCard
          detached
          className="w-16"
          title="二维码"
          icon={<Icon as={QrCodeIcon} size="xl" />}
          onPress={handleGenerateQRCodePress}
        />
        {Platform.OS !== 'web' && (
          <ActionCard
            detached
            className="w-16"
            title="朋友圈"
            icon={<Icon as={ApertureIcon} size="xl" />}
            onPress={handleShareToWechatTimeline}
          />
        )}
        {Platform.OS !== 'web' && (
          <ActionCard
            detached
            className="w-16"
            title="更多分享"
            icon={<Icon as={ShareIcon} size="xl" />}
            onPress={handleSystemSharePress}
          />
        )}
      </HStack>

      <View className="absolute pointer-events-none opacity-0">
        <QRCode
          value={publishedFormUrl}
          getRef={ref => {
            qrCodeRef.current = ref
          }}
        />
      </View>
    </VStack>
  )
}

type FormCardPreviewProps = {
  form: ShallowForm
}

function useFormHeaderImage(form: ShallowForm): ImageSourcePropType {
  return useMemo<ImageSourcePropType>(() => {
    let imageUrl
    if (form.theme?.header.type === ThemeHeaderType.Image) {
      imageUrl = form.theme.header.image?.visitUrl
    } else if (form.theme?.header.type === ThemeHeaderType.Carousel) {
      imageUrl = form.theme.header.carouselImages?.[0]?.visitUrl
    }
    return imageUrl ? { uri: imageUrl } : DefaultFormHeaderImage
  }, [form.theme])
}

function FormCardHeader({ form }: FormCardPreviewProps) {
  const headerImage = useFormHeaderImage(form)

  return (
    <View>
      <View className="aspect-[700/210] overflow-hidden w-full">
        <Image className="w-full h-full" source={headerImage} alt={form.name} />
      </View>
      <View className="px-5 pt-4 pb-1">
        <Text bold className="text-label text-center mb-3" size="title-3">
          {form.name}
        </Text>
        <Text className="text-center text-secondary-label line-clamp-3" numberOfLines={3}>
          {getHTMLText(form.description)}
        </Text>
      </View>
    </View>
  )
}

function FormCardPreview({ form }: FormCardPreviewProps) {
  const headerImage = useFormHeaderImage(form)

  return (
    <View
      className={cn('bg-background-100 mt-1 overflow-hidden', Platform.OS === 'ios' ? 'rounded-3xl' : 'rounded-xl')}
    >
      <View className="bg-white dark:bg-background-50 rounded-md overflow-hidden">
        <View className="aspect-[700/210] overflow-hidden w-full">
          <Image className="w-full h-full" source={headerImage} alt={form.name} />
        </View>
        <View className="p-4">
          <Text bold className="text-label text-center mb-4" size="title-3">
            {form.name}
          </Text>
          <Text className="text-center text-secondary-label line-clamp-3" numberOfLines={3}>
            {getHTMLText(form.description)}
          </Text>
        </View>
      </View>
    </View>
  )
}
